import { useState } from 'react'
import { motion } from 'framer-motion'

const CONNECTOR_REGISTRY = [
  { id: 'erp', name: 'ERP System', label: 'ERP', desc: 'SAP, Oracle, or other ERP for POs, inventory, and financials',
    fields: [
      { key: 'host', label: 'Host / URL', placeholder: 'https://erp.hospital.org/api' },
      { key: 'apiKey', label: 'API Key', placeholder: 'sk-...', secret: true },
      { key: 'clientId', label: 'Client ID', placeholder: 'client-id' },
    ]
  },
  { id: 'supplier', name: 'Supplier Portal', label: 'SP', desc: 'Supplier-facing portal for quotes, contracts, and compliance docs', fields: [] },
  { id: 'edi', name: 'EDI', label: 'EDI', desc: 'Electronic Data Interchange — 850, 856, 810, and more', fields: [] },
  { id: 'po', name: 'PO Database', label: 'PO', desc: 'Purchase order records, line items, approvals, and history', fields: [] },
  { id: 'invoice', name: 'Invoice System', label: 'INV', desc: 'Invoice processing, matching, and payment tracking', fields: [] },
  { id: 'email', name: 'Email', label: 'EM', desc: 'Gmail or Outlook for supplier threads and order confirmations', fields: [] },
  { id: 'slack', name: 'Slack', label: 'SL', desc: 'Procurement and alert channels for team notifications', fields: [] },
  { id: 'warehouse', name: 'Warehouse System', label: 'WH', desc: 'Inventory locations, receiving logs, and pick lists', fields: [] },
]

const ERP_DEMO_VALUES = {
  host: 'https://erp.mercygeneral.org/api/v2',
  clientId: 'mghosp-prod-01',
  apiKey: '••••••••••••••••',
}

const cardStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8,
  padding: '12px',
}

const fieldLabelStyle = {
  fontSize: 9,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'rgba(255,255,255,0.35)',
  marginBottom: 4,
  display: 'block',
}

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  padding: '7px 10px',
  color: '#FFFFFF',
  fontSize: 11,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
}

export default function ConnectorSettingsPanel({ onClose }) {
  const [connected, setConnected] = useState({ erp: true, edi: true, po: true })

  const toggleConnected = (id) =>
    setConnected(prev => ({ ...prev, [id]: !prev[id] }))

  const erp = CONNECTOR_REGISTRY[0]
  const others = CONNECTOR_REGISTRY.slice(1)

  return (
    <motion.div
      layout
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: 340, opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], layout: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }}
      style={{
        height: '100%',
        background: 'rgba(0,0,0,0.5)',
        borderRadius: 4,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
        style={{ width: 340, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}
      >
        {/* Header */}
        <div style={{ padding: '14px 16px 12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>Connections</span>
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.15, color: 'rgba(255,255,255,0.8)' }}
            whileTap={{ scale: 0.9 }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: 0, display: 'flex', alignItems: 'center' }}
          >
            <CloseIcon />
          </motion.button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Featured ERP card */}
          <div style={{ ...cardStyle, padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <ConnectorBadge label={erp.label} large />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF' }}>{erp.name}</span>
                  {connected[erp.id] && <ConnectedBadge />}
                </div>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 }}>{erp.desc}</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              {erp.fields.map(f => (
                <div key={f.key}>
                  <span style={fieldLabelStyle}>{f.label}</span>
                  <input
                    defaultValue={connected[erp.id] ? ERP_DEMO_VALUES[f.key] : ''}
                    placeholder={f.placeholder}
                    type={f.secret ? 'text' : 'text'}
                    style={inputStyle}
                  />
                </div>
              ))}
            </div>

            <ConnectButton
              connected={connected[erp.id]}
              onClick={() => toggleConnected(erp.id)}
            />
          </div>

          {/* Other connectors */}
          {others.map(c => (
            <div key={c.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
              <ConnectorBadge label={c.label} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF' }}>{c.name}</span>
                  {connected[c.id] && <ConnectedBadge />}
                </div>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4 }}>{c.desc}</span>
              </div>
              <ConnectButton
                connected={connected[c.id]}
                compact
                onClick={() => toggleConnected(c.id)}
              />
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  )
}

function ConnectorBadge({ label, large }) {
  const size = large ? 44 : 36
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: 8,
      background: 'rgba(96,165,250,0.12)',
      border: '1px solid rgba(96,165,250,0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      fontSize: large ? 12 : 10,
      fontWeight: 700,
      color: '#60A5FA',
      letterSpacing: '0.02em',
    }}>
      {label}
    </div>
  )
}

function ConnectedBadge() {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 9,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: '#4ADE80',
      background: 'rgba(74,222,128,0.12)',
      border: '1px solid rgba(74,222,128,0.3)',
      borderRadius: 999,
      padding: '2px 7px',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ADE80' }} />
      Connected
    </span>
  )
}

function ConnectButton({ connected, onClick, compact }) {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.97 }}
      style={{
        padding: compact ? '6px 12px' : '9px',
        width: compact ? 'auto' : '100%',
        flexShrink: 0,
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'inherit',
        cursor: 'pointer',
        letterSpacing: '0.03em',
        border: connected ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(96,165,250,0.5)',
        background: connected ? 'rgba(255,255,255,0.04)' : 'rgba(96,165,250,0.18)',
        color: connected ? 'rgba(255,255,255,0.55)' : '#60A5FA',
      }}
    >
      {connected ? 'Disconnect' : 'Connect'}
    </motion.button>
  )
}

function CloseIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
